import '@/styles/designtoken.css';
import '@/styles/globals.css';

import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider } from 'antd';
import type { NextPage } from 'next';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import { useRouter } from 'next/router';
import NextNProgress from 'nextjs-progressbar';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import React from 'react';
import { IntlProvider } from 'react-intl';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import PageHead from '@/components/common/PageHead';
import PlufginDector from '@/components/common/PluginDector';
import { RightProvider } from '@/components/RightProvider';
import { WithContext } from '@/components/WithContext';
import { GlobalAudioPlayerProvider } from '@/contexts/GlobalAudioPlayerContext';
import initCloudCare from '@/hooks/useCloudcare';
import initShence from '@/hooks/useShence';
import useTheme from '@/hooks/useTheme';
import en from '@/lang/en.json';
import zh from '@/lang/zh.json';
import UserStore from '@/store/User';
import theme from '@/theme/index';
import { getCookie } from '@/utils';
import { initTracker } from '@/utils/eventlog/logAdapter';
import OnlineTimeTracker from '@/utils/OnlineTimeTracker';
import { Uploader } from '@/utils/Uploader/BreakPointUploader';

const buildTime = process.env.BUILD_TIME;
const branchName = process.env.BRANCH_NAME;
const shortCommit = process.env.SHORT_COMMIT;
const appArea = process.env.APP_AREA;
const appEnv = process.env.APP_ENV;

// 定义页面类型，支持 getLayout
type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
  // 新增：页面可以指定需要在 Layout 外渲染的额外内容
  getExtraContent?: () => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function mapLocale(locale: string): string {
  if (locale.startsWith('zh')) return 'zh';
  if (locale.startsWith('en')) return 'en';
  return 'en'; // 默认英文
}

if (typeof window === 'undefined') {
  React.useLayoutEffect = () => {};
}

function NextApp({
  Component,
  pageProps,
  locale: serverLocale,
}: AppPropsWithLayout & { locale: string }) {
  const { getUserBenefit, getFunctionLimitConfig } = UserStore.getState();
  useTheme();

  // 客户端重新检测语言，解决 SSG 页面构建时 locale 固定的问题
  const [locale, setLocale] = useState(serverLocale);

  useEffect(() => {
    // 客户端：使用浏览器语言覆盖构建时的默认值
    const browserLocale = mapLocale(window.navigator.language);
    if (browserLocale !== locale) {
      setLocale(browserLocale);
    }
  }, []);

  // 获取页面的额外内容（如 SEO 部分）
  const extraContent = Component.getExtraContent?.();

  // 使用 getLayout 模式，如果页面定义了 getLayout 就使用，否则默认使用 Layout 包裹
  const getLayout = Component.getLayout ?? ((page) => page);

  useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    // 检查系统主题
    const matchDark = window.matchMedia('(prefers-color-scheme: dark)');

    // 切换 favicon 的方法
    const updateFavicon = () => {
      if (matchDark.matches) {
        favicon.href = '/logo-black.ico'; // 暗黑模式图标
      } else {
        favicon.href = '/logo-light.ico'; // 亮色模式图标
      }
    };

    // 监听主题变化
    matchDark.addEventListener('change', updateFavicon);
    updateFavicon(); // 页面加载时初始化

    return () => matchDark.removeEventListener('change', updateFavicon);
  }, []);

  // 初始化Uploader
  useEffect(() => {
    (async () => {
      // 获取用户信息
      UserStore.getState().getUserBenefit();
      const instance = await Uploader.getInstance(false);
      instance.init();
      initTracker();
    })();
  }, []);

  useEffect(() => {
    initCloudCare();
    initShence();
    getFunctionLimitConfig();

    // 初始化用户在线时长追踪
    const tracker = OnlineTimeTracker.getInstance();
    tracker.init();

    console.group('Build Info');
    console.log('\x1b[41;37m ⏳ Build Time \x1b[0m ' + buildTime); // 红色背景，白色文字，沙漏图标
    console.log('\x1b[44;37m 🌿 Branch Name \x1b[0m ' + branchName); // 蓝色背景，白色文字，绿叶图标
    console.log('\x1b[43;30m 🔗 Commit ID \x1b[0m ' + shortCommit); // 黄色背景，黑色文字，链条图标
    console.log('\x1b[45;37m 🗺️ App Area \x1b[0m ' + appArea); // 紫色背景，白色文字，地图图标
    console.log('\x1b[46;30m 🌍 App Env \x1b[0m ' + appEnv); // 青色背景，黑色文字，地球图标
    console.groupEnd();
  }, []);

  const router = useRouter();
  useEffect(() => {
    // 路由变更
    const handleRouteChange = () => {
      getUserBenefit();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    // 清除事件监听器
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <GlobalAudioPlayerProvider>
      <RightProvider>
        <IntlProvider locale={locale} messages={locale === 'en' ? en : zh}>
          <ConfigProvider theme={theme}>
            <StyleProvider hashPriority="high">
              <PageHead />
              <ErrorBoundary>
                <NextNProgress
                  color="#745CFF"
                  startPosition={0.3}
                  stopDelayMs={200}
                  height={3}
                  showOnShallow
                />
                {getLayout(<Component {...pageProps} />)}
                {extraContent}
                <PlufginDector />
              </ErrorBoundary>
              <WithContext />
            </StyleProvider>
          </ConfigProvider>
        </IntlProvider>
      </RightProvider>
    </GlobalAudioPlayerProvider>
  );
}

NextApp.getInitialProps = async (
  context: AppContext,
): Promise<AppInitialProps & { locale: string; messages: Record<string, string> }> => {
  const ctx = context.ctx;

  let locale: string;
  if (ctx.req) {
    // 服务端：使用 HTTP header
    locale = mapLocale(ctx.req.headers['accept-language'] || 'en');
  } else {
    // 客户端：使用浏览器语言
    locale = typeof window !== 'undefined' ? mapLocale(window.navigator.language) : 'en';
  }

  // 获取原始的 pageProps
  const pageProps = (await context.Component.getInitialProps?.(ctx)) || {};

  try {
    const messages = await import(`../lang/${locale}.json`);
    return {
      pageProps,
      locale,
      messages: messages.default,
    };
  } catch (error) {
    console.error('Failed to load messages', error);
    return {
      pageProps,
      locale: 'en',
      messages: {},
    };
  }
};

export default NextApp;
